import moment from 'moment';
import { create } from 'zustand';

import { CommonCursorResponse } from '@common/utils/fetcher';

import { type Match, type Message } from '@services/MatchesService';
import { type Task } from '@services/TasksService';
import { type User } from '@services/UsersService';

// Represents an action that has been optimistically applied but not yet confirmed by the backend.
type PendingAction = {
  type: 'LIKE_USER' | 'PASS_USER' | 'SEND_MESSAGE';
  payload: unknown;
  timestamp: number;
};

interface AppState {
  // Counts for tabbar indicators
  likesCount: number;
  unreadChatsCount: number;

  // Data
  likes: User[];
  matches: Map<string, Match>;
  matchesMeta: {
    pagination?: CommonCursorResponse;
    state?: 'loading' | 'loadingMore' | 'success';
  };
  messages: Map<string, Message[]>; // Keyed by matchId
  tasks: Task[]; // Tasks data for profile count
  deletedMatches: Map<string, Match>; // buffer for storing deleted matches for revert deleting if it fails
  deletedMessages: Map<string, Message[]>; // buffer for storing deleted messages for revert deleting if it fails

  // Optimistic update and sync state
  pendingActions: Map<string, PendingAction>; // Keyed by a unique action ID
  lastServerSync: number;

  // Actions to modify state
  setLikes: (likes: User[]) => void;
  setLikesCount: (count: number) => void;
  decrementLikesCount: () => void;

  optimisticallyIncrementUnreadChats: () => void;
  optimisticallyDecrementUnreadChats: () => void;
  setMatchAsRead: (matchId: string) => void;

  setMatches: (data: {
    matches: Match[];
    pagination: CommonCursorResponse;
  }) => void;
  setTasks: (tasks: Task[]) => void;
  setMatchesState: (state: AppState['matchesMeta']['state']) => void;

  addMessage: (matchId: string, message: Message) => void;
  setMessagesForMatch: (matchId: string, messages: Message[]) => void;

  getSortedMatches: () => Match[];
  optimisticallyUpdateMatchOnSend: (matchId: string, message: Message) => void;
  optimisticallyUpdateMatchBlocked: (matchId: string, blocked: boolean) => void;
  optimisticallyDeleteChat: (matchId: string, revert?: boolean) => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  // Initial State
  likesCount: 0,
  unreadChatsCount: 0,
  likes: [],
  matches: new Map(),
  matchesMeta: {},
  messages: new Map(),
  tasks: [],
  pendingActions: new Map(),
  lastServerSync: 0,
  deletedMatches: new Map(),
  deletedMessages: new Map(),

  // Actions
  setLikes: (likes) => set({ likes, likesCount: likes.length }),
  setLikesCount: (count) => set({ likesCount: count }),
  decrementLikesCount: () =>
    set((state) => ({
      likesCount: Math.max(0, state.likesCount - 1),
      // Don't modify the likes array here - the Likes component handles optimistic removals
    })),

  optimisticallyIncrementUnreadChats: () => {
    set((state) => ({
      unreadChatsCount: state.unreadChatsCount + 1,
    }));
  },

  optimisticallyDecrementUnreadChats: () => {
    set((state) => ({
      unreadChatsCount: Math.max(0, state.unreadChatsCount - 1),
    }));
  },

  setMatchAsRead: (matchId: string) => {
    set((state) => {
      const newMatches = new Map(state.matches);
      const match = newMatches.get(matchId);

      if (match) {
        newMatches.set(matchId, {
          ...match,
          unread_count: 0,
          is_unread: false,
        });
      }
      return {
        matches: newMatches,
      };
    });
  },

  setMatches: ({ matches, pagination }) => {
    const matchesMap = new Map<string, Match>();
    let unreadCount = 0;

    matches.forEach((match) => {
      // Ensure every match has a reliable date for sorting.
      // Use the last message's date, or fall back to the match date.
      const reliable_last_activity_date =
        match.last_message?.created_at || match.match_date;

      matchesMap.set(match.match_id, {
        ...match,
        last_message_date: reliable_last_activity_date,
      });

      if (match.is_unread) {
        unreadCount++;
      }
    });

    set({
      matches: matchesMap,
      unreadChatsCount: unreadCount,
      matchesMeta: { pagination },
    });
  },

  setMatchesState: (status) => {
    const state = get();
    set({ matchesMeta: { ...state.matchesMeta, state: status } });
  },

  setTasks: (tasks) => set({ tasks }),

  addMessage: (matchId, message) =>
    set((state) => {
      const newMessages = new Map(state.messages);
      const existingMessages = newMessages.get(matchId) || [];
      // Avoid duplicates
      if (existingMessages.some((m) => m.id === message.id)) {
        return {};
      }

      newMessages.set(matchId, [...existingMessages, message]);
      return { messages: newMessages };
    }),

  setMessagesForMatch: (matchId, messages) =>
    set((state) => {
      const newMessages = new Map(state.messages);
      newMessages.set(matchId, messages);

      return { messages: newMessages };
    }),

  // New: Get sorted matches using same logic as backend
  getSortedMatches: () => {
    const state = get();
    const matchesArray = Array.from(state.matches.values());

    return matchesArray.sort((a, b) => {
      // Sort purely chronologically by the last activity
      // Sort purely chronologically using the canonical last_message_date
      const dateA = a.last_message_date;
      const dateB = b.last_message_date;
      // Handle potential nulls, though the new setMatches logic should prevent this
      if (!dateA) return 1;
      if (!dateB) return -1;
      // Use moment() for reliable parsing of different date string formats (with/without timezone)
      return moment(dateB).valueOf() - moment(dateA).valueOf();
    });
  },

  // New: Optimistically update match when sending a message
  optimisticallyUpdateMatchOnSend: (matchId, message) => {
    set((state) => {
      const newMatches = new Map(state.matches);
      const match = newMatches.get(matchId);

      if (match) {
        // Create optimistic last_message object (must match Message interface)
        const optimisticLastMessage: Message = {
          id: message.id,
          match_id: matchId,
          content: message.content,
          created_at: message.created_at,
          sender_id: message.sender_id,
          sender_name: message.sender_name,
          read_at: null, // New message hasn't been read yet
          is_read: false, // New message is unread for the recipient
        };

        // Update match with new last message and current timestamp
        const updatedMatch = {
          ...match,
          last_message: optimisticLastMessage,
          last_message_date: message.created_at,
          // Don't change is_unread here - it's for the current user's perspective
        };

        newMatches.set(matchId, updatedMatch);
      }

      return { matches: newMatches };
    });
  },

  optimisticallyUpdateMatchBlocked: (matchId, blocked) => {
    set((state) => {
      const newMatches = new Map(state.matches);
      const match = newMatches.get(matchId);

      if (match) {
        const updatedMatch = {
          ...match,
          blocked_by_me: blocked,
        };

        newMatches.set(matchId, updatedMatch as Match);
      }

      return { matches: newMatches };
    });
  },

  optimisticallyDeleteChat: (matchId, revert = false) => {
    set((state) => {
      const newMatches = new Map(state.matches);
      const match = newMatches.get(matchId);
      const newDeletedMatches = new Map(state.deletedMatches);
      const newMessages = new Map(state.messages);
      const newDeletedMessages = new Map(state.deletedMessages);

      if (match && !revert) {
        // Store the match in deletedMatches buffer before deleting
        newDeletedMatches.set(matchId, match);
        newMatches.delete(matchId);

        // Store messages in deletedMessages buffer before clearing
        const messages = newMessages.get(matchId);
        if (messages) {
          newDeletedMessages.set(matchId, messages);
          newMessages.delete(matchId);
        }
      }

      if (revert) {
        const matchSource = newDeletedMatches.get(matchId);
        if (matchSource) {
          newMatches.set(matchId, matchSource);
          newDeletedMatches.delete(matchId);
        }

        // Restore messages from deletedMessages buffer
        const deletedMessages = newDeletedMessages.get(matchId);
        if (deletedMessages) {
          newMessages.set(matchId, deletedMessages);
          newDeletedMessages.delete(matchId);
        }
      }

      return {
        matches: newMatches,
        deletedMatches: newDeletedMatches,
        messages: newMessages,
        deletedMessages: newDeletedMessages,
      };
    });
  },
}));
